// "Install on this device" (home-screen app). Chrome/Edge/Android fire `beforeinstallprompt`,
// which we keep so our own prompt can trigger the browser's install dialog. iPhone/iPad Safari has
// no such API: there the user adds the app from the share menu, so we show short instructions.
import { useSyncExternalStore } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallMode = 'prompt' | 'ios' | 'none';

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // we show our own prompt at a calmer moment
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  // Other iOS browsers (CriOS, FxiOS, EdgiOS) can also add to home screen from iOS 16.4, but the
  // share-menu steps below are Safari's.
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function installMode(): InstallMode {
  if (installed || isStandalone()) return 'none';
  if (deferred) return 'prompt';
  return isIosSafari() ? 'ios' : 'none';
}

export function useInstallMode(): InstallMode {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    installMode,
    () => 'none',
  );
}

/** Opens the browser's own install dialog. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  emit();
  return outcome === 'accepted';
}

const DISMISS_KEY = 'kotipoker.installDismissedAt';
const DISMISS_MS = 14 * 24 * 3600_000;

export function installDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return Date.now() - at < DISMISS_MS;
  } catch {
    return false;
  }
}

export function dismissInstall(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // private mode: the prompt may come back next visit, which is fine
  }
}

/** Registers the service worker early so the browser treats the site as installable. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistration('/').then((r) => r ?? navigator.serviceWorker.register('/sw.js', { scope: '/' })).catch(() => undefined);
  });
}
